/**
 * CombinedShareCard Component
 *
 * Shareable card featuring BOTH Narrator + Drama Expert voices in split design.
 * Designed for the "combined" A/B test variant (balanced entertainment + utility).
 *
 * ## Visual Design
 * - **Layout**: Horizontal split (Purple top / Blue bottom)
 * - **Narrator Section**: Purple theme, episode title
 * - **Expert Section**: Blue theme, key insight
 * - **Dimensions**: 1080×1920px (9:16 Instagram Stories)
 *
 * ## Content Structure
 * ```
 * ┌────────────────────────┐
 * │  NARRATOR (Purple)     │ 50% height
 * │  "Episode N: Title"    │
 * │  Drama Score: X/10     │
 * ├────────────────────────┤ Divider
 * │  DRAMA EXPERT (Blue)   │ 50% height
 * │  "Key insight..."      │
 * │  #PatternTag           │
 * └────────────────────────┘
 * ```
 *
 * ## Usage
 * ```tsx
 * <CombinedShareCard
 *   episodeTitle="Episode 5: The Budget Battle"
 *   dramaScore={75}
 *   insightText="Your tone shifts from collaborative to defensive when deadlines are mentioned."
 *   patternTag="Defensive Language"
 * />
 * ```
 *
 * Created: 2025-01-11 (Task 2.2: Viral Share Formats)
 */

import React from "react";
import { View, Text, StyleSheet } from "react-native";
import { useTranslation } from "@dramadetector/shared";

/**
 * Props for CombinedShareCard component
 */
export interface CombinedShareCardProps {
	/**
	 * Episode title (Narrator section)
	 * Example: "Episode 5: The Budget Battle"
	 */
	episodeTitle: string;

	/**
	 * Drama score (0-10 scale from backend)
	 * Used for both Narrator score display and Expert badge color
	 */
	dramaScore: number;

	/**
	 * Key insight text (Expert section)
	 * Should be concise (1 sentence) for split layout
	 * Example: "Your tone shifts from collaborative to defensive."
	 */
	insightText: string;

	/**
	 * Pattern tag (Expert section)
	 * Example: "Defensive Language"
	 */
	patternTag: string;

	/**
	 * Optional test ID for E2E testing
	 * @default 'combined-share-card'
	 */
	testID?: string;
}

/**
 * Get drama score color based on 0-10 scale
 */
function getScoreColor(score: number): string {
	if (score <= 2) return "#10B981"; // Green (safe)
	if (score <= 5) return "#F59E0B"; // Yellow (caution)
	return "#EF4444"; // Red (high_drama/extreme_drama)
}

export function CombinedShareCard({
	episodeTitle,
	dramaScore,
	insightText,
	patternTag,
	testID = "combined-share-card",
}: CombinedShareCardProps) {
	const { t } = useTranslation("share", { useSuspense: false }) as {
		t: (key: string, options?: any) => string;
	};
	const scoreColor = getScoreColor(dramaScore);
	const scoreDisplay = dramaScore.toFixed(1);

	return (
		<View style={styles.container} testID={testID}>
			{/* NARRATOR SECTION (Top Half - Purple) */}
			<View style={styles.narratorSection} testID={`${testID}-narrator`}>
				<Text style={styles.sectionLabel}>
					{t("share_card.narrator.section_label")}
				</Text>

				<Text style={styles.episodeTitle} numberOfLines={2}>
					{episodeTitle}
				</Text>

				<View style={styles.narratorScoreContainer}>
					<Text style={[styles.narratorScore, { color: scoreColor }]}>
						{scoreDisplay}/10
					</Text>
				</View>
			</View>

			{/* DIVIDER */}
			<View style={styles.divider} testID={`${testID}-divider`}>
				<View style={styles.dividerLine} />
				<Text style={styles.dividerText}>+</Text>
				<View style={styles.dividerLine} />
			</View>

			{/* DRAMA EXPERT SECTION (Bottom Half - Blue) */}
			<View style={styles.expertSection} testID={`${testID}-expert`}>
				<Text style={styles.sectionLabel}>
					{t("share_card.expert.section_label")}
				</Text>

				<Text style={styles.insightText} numberOfLines={3}>
					{'"'}
					{insightText}
					{'"'}
				</Text>

				<View style={[styles.patternBadge, { backgroundColor: scoreColor }]}>
					<Text style={styles.patternText}>
						#{patternTag.replace(/\s+/g, "")}
					</Text>
				</View>
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		flexDirection: "column",
		alignItems: "stretch",
		justifyContent: "space-between",
	},
	// NARRATOR SECTION (Purple Theme)
	narratorSection: {
		flex: 1,
		backgroundColor: "#F5F3FF", // Purple-50
		paddingHorizontal: 40,
		paddingVertical: 48,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	sectionLabel: {
		fontSize: 14,
		fontWeight: "700",
		letterSpacing: 2,
		marginBottom: 24,
		fontFamily: "Inter",
		opacity: 0.6,
	},
	episodeTitle: {
		fontSize: 32,
		fontWeight: "700",
		color: "#581C87", // Purple-900
		textAlign: "center",
		marginBottom: 24,
		fontFamily: "Courier New",
		lineHeight: 40,
	},
	narratorScoreContainer: {
		marginTop: 16,
	},
	narratorScore: {
		fontSize: 48,
		fontWeight: "700",
		fontFamily: "Courier New",
	},
	// DIVIDER
	divider: {
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
		paddingVertical: 12,
		backgroundColor: "#FFFFFF",
	},
	dividerLine: {
		flex: 1,
		height: 2,
		backgroundColor: "#E5E7EB", // Gray-200
	},
	dividerText: {
		fontSize: 24,
		fontWeight: "700",
		color: "#6B7280", // Gray-500
		marginHorizontal: 16,
		fontFamily: "Inter",
	},
	// DRAMA EXPERT SECTION (Blue Theme)
	expertSection: {
		flex: 1,
		backgroundColor: "#EFF6FF", // Blue-50
		paddingHorizontal: 40,
		paddingVertical: 48,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	insightText: {
		fontSize: 24,
		fontWeight: "600",
		color: "#1E3A8A", // Blue-900
		textAlign: "center",
		marginBottom: 24,
		fontFamily: "Inter",
		lineHeight: 32,
	},
	patternBadge: {
		paddingHorizontal: 20,
		paddingVertical: 10,
		borderRadius: 20,
		marginTop: 16,
	},
	patternText: {
		fontSize: 16,
		fontWeight: "700",
		color: "#FFFFFF",
		fontFamily: "Inter",
	},
});
